import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiJson, ApiError } from "../../../lib/api";

import { getTenantUuid } from "../../ingress/tenant"; // adjust path if needed

const TENANT_UUID =
  (getTenantUuid() || "00000000-0000-0000-0000-000000000001").trim() ||
  "00000000-0000-0000-0000-000000000001";

// NOTE: flows endpoints now accept UUID-or-slug, so we pass TENANT_UUID.
// Do NOT hardcode "root" here; it causes cross-tenant leakage + 403s.

type IngressApp = {
  id: string;
  tenant_id?: string;
  tenant_label?: string; // present in backend
  slug: string;
  name: string;
  description?: string | null;
  is_published: boolean;
  flow_id: string;
  version_id: string;
  auth_mode?: string | null;
  rate_limit_rps?: number | null;
  created_at?: string;
  updated_at?: string;
};

type IngressSchema = {
  input_json_schema?: any;
  ui_schema?: any;
  output_rules?: any;
  // backend admin schema has more fields, but we only need these
};

type FlowListItem = {
  id: string;
  name: string;
  version?: number | null;
};

type FlowVersionItem = {
  id: string;
  version: number;
  version_hash?: string;
  created_at?: string | null;
  created_by?: string | null;
  changelog?: string | null;
  is_latest?: boolean;
};

type AdminIngressRequest = {
  id: string;
  status: string;
  run_id?: string | null;
  error?: string | null;
  created_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  app_id?: string;
};

type ApiKeyRow = {
  id: string;
  tenant_id: string;
  app_id: string;
  key_prefix: string;
  created_at: string;
  revoked_at?: string | null;
};

type ApiKeyCreateResp = {
  id: string;
  tenant_id: string;
  app_id: string;
  key_prefix: string;
  api_key: string; // plaintext shown once
  created_at: string;
};

/** NEW: External App Connections types */
type ConnectionRow = {
  id: string;
  tenant_id: string;
  app_id: string;
  name: string;
  type: string;
  base_url?: string | null;
  notes?: string | null;
  auth_scheme: string;
  key_prefix: string;
  created_at: string;
  revoked_at?: string | null;
  last_used_at?: string | null;
  last_test_at?: string | null;
  last_test_status?: string | null;
};

type ConnectionCreateResp = ConnectionRow & { api_key: string };

type ConnectionTestResp = {
  ok: boolean;
  request_id?: string | null;
  status?: string | null;
  error?: string | null;
  final_output_kind?: string | null;
  final_output?: any;
  final_output_path?: string | null;
  timings_ms?: Record<string, number>;
};

function pretty(v: any) {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function parseJson(text: string) {
  const t = text.trim();
  if (!t) return null;
  return JSON.parse(t);
}

function fmtDt(s?: string | null) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return String(s);
  }
}

function safeString(v: any) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeInt(v: any, fallback: number) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function looksLikeJsonString(s: string) {
  const t = (s || "").trim();
  return t.startsWith("{") || t.startsWith("[");
}

function unwrapFanoutEnvelope(maybeEnvelope: string): string | null {
  const s = (maybeEnvelope || "").trim();
  if (!s) return null;

  // try JSON.parse
  if (looksLikeJsonString(s)) {
    try {
      const obj = JSON.parse(s);
      const t =
        obj?.output?.text ??
        obj?.output?.output?.text ??
        obj?.output?.winner_output?.text ??
        obj?.winner_output?.text ??
        obj?.text;
      if (typeof t === "string" && t.trim()) return t;
    } catch {
      // regex fallback below
    }
  }

  // regex fallback: unescaped or escaped "text":"..."
  let m = s.match(/"text"\s*:\s*"([^"]+)"/);
  if (m?.[1]) return m[1];
  m = s.match(/\\"text\\"\s*:\s*\\"([^\\"]+)\\"/);
  if (m?.[1]) return m[1];

  return null;
}

function extractPreviewFinalAnswer(previewOut: any): string | null {
  if (!previewOut) return null;

  // prefer server-side contract
  if (previewOut.final_output !== undefined) {
    if (typeof previewOut.final_output === "string") {
      return unwrapFanoutEnvelope(previewOut.final_output) ?? previewOut.final_output;
    }
    if (typeof previewOut.final_output === "object" && previewOut.final_output !== null) {
      return JSON.stringify(previewOut.final_output, null, 2);
    }
    return String(previewOut.final_output ?? "");
  }

  // fallback: output_json.outputs heuristic
  const outputs = previewOut?.output_json?.outputs;
  if (!outputs || typeof outputs !== "object") return null;

  const keys = Object.keys(outputs);
  if (!keys.length) return null;

  const preferred =
    keys.length === 1 ? keys[0] : (["make_text", "agg", "llm1"].find((k) => keys.includes(k)) || keys[0]);

  const primary = (outputs as any)[preferred];
  if (typeof primary === "string") return unwrapFanoutEnvelope(primary) ?? primary;
  if (primary && typeof primary === "object") {
    if (typeof primary.text === "string") return unwrapFanoutEnvelope(primary.text) ?? primary.text;
    return JSON.stringify(primary, null, 2);
  }
  return null;
}

function isObject(v: any): v is Record<string, any> {
  return v && typeof v === "object" && !Array.isArray(v);
}

function getRequiredKeys(inputSchema: any): string[] {
  const req = isObject(inputSchema) && Array.isArray((inputSchema as any).required) ? (inputSchema as any).required : [];
  return req.filter((x: any) => typeof x === "string" && x.trim().length > 0);
}

function getPropertySchema(inputSchema: any, key: string): any {
  const props = isObject(inputSchema) && isObject((inputSchema as any).properties) ? (inputSchema as any).properties : {};
  return (props as any)[key];
}

function isEmptyValue(v: any): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim().length === 0;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function AdminIngressAppDetailPage() {
  const { appId } = useParams();

  const [app, setApp] = useState<IngressApp | null>(null);

  // Edit mode (Issue #1 + #5)
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<IngressApp | null>(null);

  // Tabs (Issue #3 + #6 + Universal Ingress)
  const [tab, setTab] = useState<"overview" | "schema" | "preview" | "requests" | "connections" | "api_keys">("overview");

  // Schema editor (Issue #3)
  const [schemaText, setSchemaText] = useState<string>("");

  // Create/app updates: flow/version dropdown + manual override (Issue #2)
  const [flows, setFlows] = useState<FlowListItem[]>([]);
  const [versions, setVersions] = useState<FlowVersionItem[]>([]);
  const [flowMode, setFlowMode] = useState<"dropdown" | "manual">("dropdown");
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Requests list (Issue #6)
  const [requests, setRequests] = useState<AdminIngressRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  // Runner preview state (Issue #3 + #4) + required-field validation fix
  const [previewForm, setPreviewForm] = useState<Record<string, any>>({});
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewOut, setPreviewOut] = useState<any>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [previewFieldErrs, setPreviewFieldErrs] = useState<Record<string, string>>({});

  // Universal Plug-In Ingress: API keys (kept as-is)
  const [keysLoading, setKeysLoading] = useState(false);
  const [keysErr, setKeysErr] = useState<string | null>(null);
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreateResp | null>(null);
  const [creatingKey, setCreatingKey] = useState(false);
  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);

  // NEW: Connections state
  const [connLoading, setConnLoading] = useState(false);
  const [connErr, setConnErr] = useState<string | null>(null);
  const [connections, setConnections] = useState<ConnectionRow[]>([]);
  const [newConnName, setNewConnName] = useState("");
  const [newConnType, setNewConnType] = useState("custom");
  const [newConnBaseUrl, setNewConnBaseUrl] = useState("");
  const [newConnNotes, setNewConnNotes] = useState("");
  const [createdConnKey, setCreatedConnKey] = useState<ConnectionCreateResp | null>(null);
  const [testingConnId, setTestingConnId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<ConnectionTestResp | null>(null);
  const [rotatingConnId, setRotatingConnId] = useState<string | null>(null);
  const [revokingConnId2, setRevokingConnId2] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const publicUrl = useMemo(() => (app ? `/app/ingress/${app.slug}` : ""), [app]);
  const [authModeBusy, setAuthModeBusy] = useState(false);
  const [authModeErr, setAuthModeErr] = useState<string | null>(null);

  // Parse schema blob into the 3 backend fields
  const parsedSchema = useMemo(() => {
    try {
      const obj = parseJson(schemaText) || {};
      return {
        ok: true as const,
        input_json_schema: obj.input_json_schema ?? { type: "object" },
        ui_schema: obj.ui_schema ?? { title: "Run", fields: [] },
        output_rules: obj.output_rules ?? {},
      };
    } catch (e: any) {
      return { ok: false as const, error: e?.message || String(e) };
    }
  }, [schemaText]);

  // Determine preview fields from schema
  const previewFields = useMemo(() => {
    if (!parsedSchema.ok) return [];
    const ui = parsedSchema.ui_schema;
    const input = parsedSchema.input_json_schema;

    const uiFields = Array.isArray(ui?.fields) ? ui.fields : [];
    if (uiFields.length) {
      return uiFields
        .map((f: any) => ({
          key: safeString(f.key),
          label: safeString(f.label || f.key || "field"),
          widget: safeString(f.widget || "text"),
          placeholder: safeString(f.placeholder || ""),
        }))
        .filter((f: any) => !!f.key);
    }

    const propsObj = input?.properties && typeof input.properties === "object" ? input.properties : {};
    return Object.keys(propsObj).map((k) => ({
      key: k,
      label: k,
      widget: "text",
      placeholder: "",
    }));
  }, [parsedSchema]);

  const requiredKeys = useMemo(() => (parsedSchema.ok ? getRequiredKeys(parsedSchema.input_json_schema) : []), [parsedSchema]);

  function validatePreviewForm(): { ok: boolean; errors: Record<string, string> } {
    const errors: Record<string, string> = {};
    if (!parsedSchema.ok) return { ok: false, errors: { _schema: "Fix schema JSON first." } };

    for (const k of requiredKeys) {
      const v = previewForm[k];
      if (isEmptyValue(v)) {
        errors[k] = "Required";
        continue;
      }
      const prop = getPropertySchema(parsedSchema.input_json_schema, k);
      if (typeof v === "string" && isObject(prop)) {
        const minL = (prop as any).minLength;
        const maxL = (prop as any).maxLength;
        if (typeof minL === "number" && v.trim().length < minL) errors[k] = `Min length ${minL}`;
        if (typeof maxL === "number" && v.trim().length > maxL) errors[k] = `Max length ${maxL}`;
      }
    }
    return { ok: Object.keys(errors).length === 0, errors };
  }

  async function loadAppAndSchema() {
    if (!appId) return;
    setLoading(true);
    setErr(null);
    setMsg(null);
    try {
      const a = await apiJson<IngressApp>(`/v1/admin/ingress/apps/${appId}?tenant_id=${TENANT_UUID}`);
      setApp(a);
      setDraft(a);

      try {
        const s = await apiJson<IngressSchema>(`/v1/admin/ingress/apps/${appId}/schema?tenant_id=${TENANT_UUID}`);
        const schema: IngressSchema = s || {};
        setSchemaText(
          pretty({
            input_json_schema: schema.input_json_schema || { type: "object" },
            ui_schema: schema.ui_schema || { title: "Run", fields: [{ key: "text", label: "Input Text", widget: "textarea" }] },
            output_rules: schema.output_rules || { extract_text_field: "text" },
          })
        );
      } catch {
        setSchemaText(
          pretty({
            input_json_schema: { "$schema": "http://json-schema.org/draft-07/schema#", type: "object", properties: {}, additionalProperties: true },
            ui_schema: { title: "Run", fields: [] },
            output_rules: {},
          })
        );
      }
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
      setApp(null);
      setDraft(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadFlows() {
    setLoadingFlows(true);
    try {
      const list = await apiJson<any>(`/v1/flows?tenant_id=${encodeURIComponent(TENANT_UUID)}`);
      const items: FlowListItem[] = Array.isArray(list)
        ? list.map((x: any) => ({
            id: safeString(x.id),
            name: safeString(x.name || x.id),
            version: x.version ?? null,
          }))
        : [];
      setFlows(items);
    } catch {
      setFlows([]);
    } finally {
      setLoadingFlows(false);
    }
  }

  async function loadVersions(flowId: string) {
    if (!flowId) {
      setVersions([]);
      return;
    }
    setLoadingVersions(true);
    try {
      const resp = await apiJson<any>(
        `/v1/flows/${encodeURIComponent(flowId)}/versions?tenant_id=${encodeURIComponent(TENANT_UUID)}`
      );
      const vs: FlowVersionItem[] = Array.isArray(resp?.versions)
        ? resp.versions.map((v: any) => ({
            id: safeString(v.id),
            version: safeInt(v.version, 0),
            version_hash: v.version_hash,
            created_at: v.created_at ?? null,
            created_by: v.created_by ?? null,
            changelog: v.changelog ?? null,
            is_latest: !!v.is_latest,
          }))
        : [];
      setVersions(vs);
    } catch {
      setVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  }

  async function loadRequestsForApp(appId2: string) {
    if (!appId2) return;
    setLoadingRequests(true);
    try {
      const qs = new URLSearchParams();
      qs.set("tenant_id", TENANT_UUID);
      qs.set("limit", "50");
      qs.set("app_id", appId2);

      const rows = await apiJson<any>(`/v1/admin/ingress/requests?${qs.toString()}`);
      const items: AdminIngressRequest[] = Array.isArray(rows)
        ? rows.map((r: any) => ({
            id: safeString(r.id),
            status: safeString(r.status),
            run_id: r.run_id ?? null,
            error: r.error ?? null,
            created_at: safeString(r.created_at),
            started_at: r.started_at ?? null,
            finished_at: r.finished_at ?? null,
            app_id: r.app_id,
          }))
        : [];
      setRequests(items);
    } catch (e: any) {
      setRequests([]);
      console.warn("loadRequestsForApp error", e);
    } finally {
      setLoadingRequests(false);
    }
  }

  async function loadApiKeys() {
    if (!appId) return;
    setKeysLoading(true);
    setKeysErr(null);
    try {
      const rows = await apiJson<any>(`/v1/admin/ingress/apps/${appId}/api_keys?tenant_id=${TENANT_UUID}`);
      const items: ApiKeyRow[] = Array.isArray(rows)
        ? rows.map((r: any) => ({
            id: safeString(r.id),
            tenant_id: safeString(r.tenant_id),
            app_id: safeString(r.app_id),
            key_prefix: safeString(r.key_prefix),
            created_at: safeString(r.created_at),
            revoked_at: r.revoked_at ?? null,
          }))
        : [];
      setKeys(items);
    } catch (e: any) {
      setKeysErr(e instanceof ApiError ? e.message : String(e));
      setKeys([]);
    } finally {
      setKeysLoading(false);
    }
  }

  async function createApiKey() {
    if (!appId) return;
    setCreatingKey(true);
    setKeysErr(null);
    setCreatedKey(null);
    try {
      const resp = await apiJson<ApiKeyCreateResp>(`/v1/admin/ingress/apps/${appId}/api_keys?tenant_id=${TENANT_UUID}`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      setCreatedKey(resp);
      await loadApiKeys();
    } catch (e: any) {
      setKeysErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setCreatingKey(false);
    }
  }

  async function revokeApiKey(keyId: string) {
    if (!appId) return;
    setRevokingKeyId(keyId);
    setKeysErr(null);
    try {
      await apiJson(`/v1/admin/ingress/apps/${appId}/api_keys/${keyId}/revoke?tenant_id=${TENANT_UUID}`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await loadApiKeys();
    } catch (e: any) {
      setKeysErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setRevokingKeyId(null);
    }
  }

  /** NEW: Connections API calls */
  async function loadConnections() {
    if (!appId) return;
    setConnLoading(true);
    setConnErr(null);
    try {
      const rows = await apiJson<any>(`/v1/admin/ingress/apps/${appId}/connections?tenant_id=${TENANT_UUID}`);
      const items: ConnectionRow[] = Array.isArray(rows)
        ? rows.map((r: any) => ({
            id: safeString(r.id),
            tenant_id: safeString(r.tenant_id),
            app_id: safeString(r.app_id),
            name: safeString(r.name),
            type: safeString(r.type),
            base_url: r.base_url ?? null,
            notes: r.notes ?? null,
            auth_scheme: safeString(r.auth_scheme || "ingress_key"),
            key_prefix: safeString(r.key_prefix),
            created_at: safeString(r.created_at),
            revoked_at: r.revoked_at ?? null,
            last_used_at: r.last_used_at ?? null,
            last_test_at: r.last_test_at ?? null,
            last_test_status: r.last_test_status ?? null,
          }))
        : [];
      setConnections(items);
    } catch (e: any) {
      setConnErr(e instanceof ApiError ? e.message : String(e));
      setConnections([]);
    } finally {
      setConnLoading(false);
    }
  }

  async function createConnection() {
    if (!appId) return;
    setConnErr(null);
    setCreatedConnKey(null);

    const name = newConnName.trim();
    if (!name) {
      setConnErr("Connection name is required.");
      return;
    }

    try {
      const resp = await apiJson<ConnectionCreateResp>(`/v1/admin/ingress/apps/${appId}/connections?tenant_id=${TENANT_UUID}`, {
        method: "POST",
        body: JSON.stringify({
          name,
          type: (newConnType || "custom").trim(),
          base_url: newConnBaseUrl.trim() || null,
          notes: newConnNotes.trim() || null,
          auth_scheme: "ingress_key",
        }),
      });
      setCreatedConnKey(resp);
      setNewConnName("");
      setNewConnBaseUrl("");
      setNewConnNotes("");
      await loadConnections();
    } catch (e: any) {
      setConnErr(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function revokeConnection(connId: string) {
    if (!appId) return;
    setRevokingConnId2(connId);
    setConnErr(null);
    try {
      await apiJson(`/v1/admin/ingress/apps/${appId}/connections/${connId}/revoke?tenant_id=${TENANT_UUID}`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await loadConnections();
    } catch (e: any) {
      setConnErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setRevokingConnId2(null);
    }
  }

  async function rotateConnection(connId: string) {
    if (!appId) return;
    setRotatingConnId(connId);
    setConnErr(null);
    setCreatedConnKey(null);
    try {
      const resp = await apiJson<ConnectionCreateResp>(
        `/v1/admin/ingress/apps/${appId}/connections/${connId}/rotate?tenant_id=${TENANT_UUID}`,
        { method: "POST", body: JSON.stringify({}) }
      );
      setCreatedConnKey(resp);
      await loadConnections();
    } catch (e: any) {
      setConnErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setRotatingConnId(null);
    }
  }

  async function testConnection(connId: string) {
    if (!appId) return;
    setTestingConnId(connId);
    setConnErr(null);
    setTestResult(null);
    try {
      const resp = await apiJson<ConnectionTestResp>(
        `/v1/admin/ingress/apps/${appId}/connections/${connId}/test?tenant_id=${TENANT_UUID}`,
        { method: "POST", body: JSON.stringify({ inputs_override: {} }) }
      );
      setTestResult(resp);
      await loadConnections();
    } catch (e: any) {
      setConnErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setTestingConnId(null);
    }
  }

  useEffect(() => {
    loadAppAndSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  useEffect(() => {
    if (editMode) loadFlows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  useEffect(() => {
    if (!editMode) return;
    const fid = draft?.flow_id || "";
    if (fid) loadVersions(fid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, draft?.flow_id]);

  useEffect(() => {
    if (tab !== "requests") return;
    if (app?.id) loadRequestsForApp(app.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, app?.id]);

  useEffect(() => {
    if (tab !== "connections") return;
    loadConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, appId]);

  useEffect(() => {
    if (tab !== "api_keys") return;
    loadApiKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, appId]);

  function setDraftField<K extends keyof IngressApp>(k: K, v: any) {
    setDraft((d) => (d ? ({ ...d, [k]: v } as IngressApp) : d));
  }

  async function patchAppFull(next: Partial<IngressApp>, successMsg: string) {
    if (!appId || !app || !draft) return;
    setErr(null);
    setMsg(null);

    const body = {
      tenant_label: safeString(
        (next as any).tenant_label ??
          (draft as any).tenant_label ??
          (app as any).tenant_label ??
          ""
      ),
      slug: safeString(next.slug ?? draft.slug ?? app.slug),
      name: safeString(next.name ?? draft.name ?? app.name),
      description: (next.description ?? draft.description ?? app.description) ?? null,
      flow_id: safeString(next.flow_id ?? draft.flow_id ?? app.flow_id),
      version_id: safeString(next.version_id ?? draft.version_id ?? app.version_id),
      auth_mode: safeString(next.auth_mode ?? draft.auth_mode ?? app.auth_mode ?? "tenant"),
      rate_limit_rps: safeInt(next.rate_limit_rps ?? draft.rate_limit_rps ?? app.rate_limit_rps ?? 5, 5),
      is_published: !!(next.is_published ?? draft.is_published ?? app.is_published),
    };

    await apiJson(`/v1/admin/ingress/apps/${appId}?tenant_id=${TENANT_UUID}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });

    setMsg(successMsg);
    await loadAppAndSchema();
  }

  async function saveEdits() {
    if (!app || !draft) return;
    try {
      await patchAppFull(
        {
          tenant_label: (draft as any).tenant_label,
          slug: draft.slug,
          name: draft.name,
          description: draft.description,
          flow_id: draft.flow_id,
          version_id: draft.version_id,
          auth_mode: draft.auth_mode,
          rate_limit_rps: draft.rate_limit_rps,
          is_published: draft.is_published,
        } as any,
        "App updated."
      );
      setEditMode(false);
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  }
  async function setAuthMode(mode: "tenant" | "api_key") {
    if (!appId) return;
    setAuthModeBusy(true);
    setAuthModeErr(null);
    setErr(null);
    setMsg(null);
    try {
      await apiJson(`/v1/admin/ingress/apps/${appId}/set_auth_mode?tenant_id=${TENANT_UUID}`, {
        method: "POST",
        body: JSON.stringify({ auth_mode: mode }),
      });
      setMsg(`Auth mode set to "${mode}".`);
      await loadAppAndSchema();
      setDraft((d) => (d ? ({ ...d, auth_mode: mode } as IngressApp) : d));
    } catch (e: any) {
      const m = e instanceof ApiError ? e.message : String(e);
      setAuthModeErr(m);
    } finally {
      setAuthModeBusy(false);
    }
  }

  async function togglePublished() {
    if (!app) return;
    try {
      await patchAppFull({ is_published: !app.is_published }, !app.is_published ? "App published." : "App unpublished.");
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function saveSchema() {
    if (!appId) return;
    setErr(null);
    setMsg(null);
    try {
      if (!parsedSchema.ok) throw new Error("Schema JSON is invalid. Fix JSON before saving.");
      await apiJson(`/v1/admin/ingress/apps/${appId}/schema?tenant_id=${TENANT_UUID}`, {
        method: "PUT",
        body: JSON.stringify({
          input_json_schema: parsedSchema.input_json_schema ?? {},
          ui_schema: parsedSchema.ui_schema ?? {},
          output_rules: parsedSchema.output_rules ?? {},
        }),
      });
      setMsg("Schema saved.");
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function runPreview() {
    if (!app) return;

    const v = validatePreviewForm();
    setPreviewFieldErrs(v.errors);
    if (!v.ok) {
      setPreviewErr("Please fill all required fields.");
      return;
    }

    setPreviewBusy(true);
    setPreviewErr(null);
    setPreviewOut(null);
    try {
      const resp = await apiJson<any>(`/v1/ingress/apps/${encodeURIComponent(app.slug)}/requests?tenant_id=${TENANT_UUID}`, {
        method: "POST",
        body: JSON.stringify({ inputs: previewForm }),
      });

      const requestId = resp?.request_id;
      if (!requestId || requestId === "null") throw new Error("Ingress did not return a request_id.");

      for (let i = 0; i < 80; i++) {
        const st = await apiJson<any>(`/v1/ingress/requests/${requestId}?tenant_id=${TENANT_UUID}`);
        if (st?.status === "succeeded" || st?.status === "failed") {
          if (st.status === "failed") throw new Error(st.error || "Ingress request failed");
          const out = await apiJson<any>(`/v1/ingress/requests/${requestId}/output?tenant_id=${TENANT_UUID}`);
          setPreviewOut(out);
          return;
        }
        await new Promise((r) => setTimeout(r, 500));
      }
      throw new Error("Timeout waiting for preview request.");
    } catch (e: any) {
      setPreviewErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPreviewBusy(false);
    }
  }

  if (loading) return <div className="p-6">Loading…</div>;

  if (!app) {
    return (
      <div className="p-6">
        <div className="text-xl font-semibold">Ingress app not found</div>
        {err && <div className="mt-2 text-sm text-red-600 whitespace-pre-wrap">{err}</div>}
        <div className="mt-4">
          <Link className="text-blue-700 hover:underline" to="/app/admin/ingress/apps">
            Back to Apps
          </Link>
        </div>
      </div>
    );
  }

  const isPublished = !!app.is_published;
  const runnerRoute = app.slug ? `/app/ingress/${app.slug}` : "/app/ingress";

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs text-gray-500">
            <Link className="hover:underline" to="/app/admin/ingress/apps">
              Ingress Apps
            </Link>{" "}
            / {app.name}
          </div>
          <h2 className="text-xl font-semibold mt-1">{app.name}</h2>
          <div className="text-sm text-gray-600 mt-1">
            slug: <code>{app.slug}</code> · flow: <code>{app.flow_id}</code> · version: <code>{app.version_id}</code>
          </div>
        </div>

        <div className="flex gap-2">
          <a
            className={`px-3 py-2 rounded border hover:bg-gray-50 ${!isPublished ? "opacity-60 pointer-events-none" : ""}`}
            href={runnerRoute}
            target="_blank"
            rel="noreferrer"
            title={isPublished ? "Open end-user runner for this slug" : "Publish app to enable runner"}
          >
            Open Runner
          </a>

          <button
            className="px-3 py-2 rounded border hover:bg-gray-50"
            type="button"
            onClick={() => {
              setErr(null);
              setMsg(null);
              setEditMode((v) => !v);
              setDraft(app);
            }}
          >
            {editMode ? "Cancel" : "Edit"}
          </button>

          <button className="px-3 py-2 rounded bg-gray-900 text-white hover:bg-black" type="button" onClick={togglePublished}>
            {isPublished ? "Unpublish" : "Publish"}
          </button>
        </div>
      </div>

      {msg && <div className="mt-4 text-sm text-green-700">{msg}</div>}
      {err && <div className="mt-4 text-sm text-red-600 whitespace-pre-wrap">{err}</div>}

      {/* Tabs */}
      <div className="mt-6 border-b flex gap-2 flex-wrap">
        <TabBtn label="Overview" active={tab === "overview"} onClick={() => setTab("overview")} />
        <TabBtn label="Schema" active={tab === "schema"} onClick={() => setTab("schema")} />
        <TabBtn label="Preview" active={tab === "preview"} onClick={() => setTab("preview")} />
        <TabBtn label="Requests" active={tab === "requests"} onClick={() => setTab("requests")} />
        <TabBtn label="Connections" active={tab === "connections"} onClick={() => setTab("connections")} />
        <TabBtn label="API Keys" active={tab === "api_keys"} onClick={() => setTab("api_keys")} />
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded border bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="font-medium">App Settings</div>
              {editMode && (
                <button className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" type="button" onClick={saveEdits}>
                  Save
                </button>
              )}
            </div>

            <div className="mt-3 text-sm space-y-2">
              <KV label="ID" value={<code>{app.id}</code>} />
              <KV label="Published" value={isPublished ? "Yes" : "No"} />

              <FieldRow label="Slug" mono edit={editMode} value={safeString(draft?.slug)} onChange={(v) => setDraftField("slug", v)} />
              <FieldRow label="Name" edit={editMode} value={safeString(draft?.name)} onChange={(v) => setDraftField("name", v)} />

              <div>
                <div className="text-gray-600 text-xs">Description</div>
                {editMode ? (
                  <textarea
                    className="mt-1 w-full border rounded p-2 text-sm"
                    rows={3}
                    value={safeString(draft?.description)}
                    onChange={(e) => setDraftField("description", e.target.value)}
                  />
                ) : (
                  <div className="mt-1">{app.description || <span className="text-gray-400">—</span>}</div>
                )}
              </div>

                            <div>
                <div className="text-gray-600 text-xs">Auth Mode</div>

                {editMode ? (
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <select
                      className="border rounded p-2 text-sm"
                      value={(safeString(draft?.auth_mode || "tenant") as any) === "api_key" ? "api_key" : "tenant"}
                      onChange={(e) => setDraftField("auth_mode", e.target.value)}
                    >
                      <option value="tenant">tenant (no key required)</option>
                      <option value="api_key">api_key (requires X-Ingress-Key)</option>
                    </select>

                    <button
                      className="px-3 py-2 rounded border hover:bg-gray-50 text-sm disabled:opacity-60"
                      type="button"
                      disabled={authModeBusy}
                      onClick={() =>
                        setAuthMode(((safeString(draft?.auth_mode || "tenant") as any) === "api_key" ? "api_key" : "tenant") as
                          | "tenant"
                          | "api_key")
                      }
                      title="Applies immediately via /set_auth_mode"
                    >
                      {authModeBusy ? "Applying…" : "Apply"}
                    </button>

                    <div className="text-xs text-gray-500">
                      Applies immediately. Keys are only required when auth_mode=api_key.
                    </div>
                  </div>
                ) : (
                  <div className="mt-1 text-sm">
                    <code>{safeString(app.auth_mode || "tenant")}</code>
                  </div>
                )}

                {authModeErr && <div className="mt-2 text-sm text-red-700 whitespace-pre-wrap">{authModeErr}</div>}
              </div>
              <FieldRow label="Rate limit (RPS)" edit={editMode} value={String(draft?.rate_limit_rps ?? 5)} onChange={(v) => setDraftField("rate_limit_rps", safeInt(v, 5))} type="number" />

              <div className="pt-2 border-t">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Pinned Flow Version</div>
                  {editMode && (
                    <div className="flex items-center gap-2 text-xs">
                      <button className={`px-2 py-1 rounded border ${flowMode === "dropdown" ? "bg-gray-900 text-white" : "hover:bg-gray-50"}`} onClick={() => setFlowMode("dropdown")} type="button">Dropdown</button>
                      <button className={`px-2 py-1 rounded border ${flowMode === "manual" ? "bg-gray-900 text-white" : "hover:bg-gray-50"}`} onClick={() => setFlowMode("manual")} type="button">Manual IDs</button>
                    </div>
                  )}
                </div>

                {!editMode && (
                  <div className="mt-2 text-sm text-gray-700">
                    flow: <code>{app.flow_id}</code> · version: <code>{app.version_id}</code>
                  </div>
                )}

                {editMode && flowMode === "manual" && (
                  <div className="mt-2 space-y-2">
                    <FieldRow label="Flow ID" mono edit={true} value={safeString(draft?.flow_id)} onChange={(v) => setDraftField("flow_id", v)} />
                    <FieldRow label="Version ID" mono edit={true} value={safeString(draft?.version_id)} onChange={(v) => setDraftField("version_id", v)} />
                    <div className="text-xs text-gray-500">Tip: You can copy flow/version IDs from the Workflow page or Flow Versions list.</div>
                  </div>
                )}

                {editMode && flowMode === "dropdown" && (
                  <div className="mt-2 space-y-2">
                    <div className="text-xs text-gray-600">Select Flow</div>
                    <select className="w-full border rounded p-2 text-sm" value={safeString(draft?.flow_id)} onChange={(e) => setDraftField("flow_id", e.target.value)} disabled={loadingFlows}>
                      <option value="">{loadingFlows ? "Loading flows…" : "Select a flow"}</option>
                      {flows.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name} ({f.id.slice(0, 8)}…)
                        </option>
                      ))}
                    </select>

                    <div className="text-xs text-gray-600">Select Version</div>
                    <select className="w-full border rounded p-2 text-sm" value={safeString(draft?.version_id)} onChange={(e) => setDraftField("version_id", e.target.value)} disabled={loadingVersions || !draft?.flow_id}>
                      <option value="">{!draft?.flow_id ? "Select flow first" : loadingVersions ? "Loading versions…" : "Select a version"}</option>
                      {versions.map((v) => (
                        <option key={v.id} value={v.id}>
                          v{v.version}{v.is_latest ? " (latest)" : ""} — {v.id.slice(0, 8)}…
                        </option>
                      ))}
                    </select>

                    <div className="text-xs text-gray-500">
                      Uses <code>GET /v1/flows?tenant_id={TENANT_UUID}</code> and{" "}
                      <code>/v1/flows/:id/versions?tenant_id={TENANT_UUID}</code>.
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-2 border-t text-xs text-gray-500">
                Created: {fmtDt(app.created_at)} · Updated: {fmtDt(app.updated_at)}
              </div>
            </div>
          </div>

          <div className="rounded border bg-white p-4">
            <div className="font-medium">Runner URL</div>
            <div className="mt-2 text-sm text-gray-700">
              <code>{runnerRoute}</code>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              End-user runner route: <code>/app/ingress/&lt;slug&gt;</code> (opens in new tab).
            </div>
            <div className="mt-3 text-xs text-gray-500">
              Ingress API calls use query param <code>tenant_id={TENANT_UUID}</code> and header <code>X-Tenant-ID: root</code>.
            </div>
          </div>
        </div>
      )}

      {/* SCHEMA */}
      {tab === "schema" && (
        <div className="mt-6 rounded border bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-medium">Schema + UI schema</div>
              <div className="mt-1 text-xs text-gray-500">
                Saved via <code>PUT /v1/admin/ingress/apps/:id/schema?tenant_id={TENANT_UUID}</code>. This expects{" "}
                <code>{"{ input_json_schema, ui_schema, output_rules }"}</code>.
              </div>
            </div>
            <button className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60" onClick={saveSchema} disabled={!parsedSchema.ok} title={!parsedSchema.ok ? "Fix JSON errors before saving" : "Save schema"} type="button">
              Save Schema
            </button>
          </div>

          {!parsedSchema.ok && <div className="mt-3 text-sm text-red-700">Invalid JSON: {parsedSchema.error}</div>}

          <textarea className="mt-3 w-full min-h-[420px] border rounded p-3 font-mono text-xs" value={schemaText} onChange={(e) => setSchemaText(e.target.value)} />
        </div>
      )}

      {/* PREVIEW */}
      {tab === "preview" && (
        <div className="mt-6 rounded border bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-medium">Runner Preview</div>
              <div className="mt-1 text-xs text-gray-500">
                Submits to <code>POST /v1/ingress/apps/{app.slug}/requests?tenant_id={TENANT_UUID}</code>, polls status, then fetches output.
              </div>
            </div>
            <button className="px-3 py-2 rounded bg-gray-900 text-white hover:bg-black disabled:opacity-60" onClick={runPreview} disabled={previewBusy || !parsedSchema.ok} type="button">
              {previewBusy ? "Running…" : "Run Preview"}
            </button>
          </div>

          {!parsedSchema.ok ? (
            <div className="mt-4 text-sm text-red-700">Fix schema JSON first (Schema tab).</div>
          ) : previewFields.length === 0 ? (
            <div className="mt-4 text-sm text-gray-600">No fields found (add ui_schema.fields or input_json_schema.properties).</div>
          ) : (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {previewFields.map((f: any) => {
                const isReq = requiredKeys.includes(f.key);
                const fieldErr = previewFieldErrs[f.key];
                return (
                  <div key={f.key} className="flex flex-col">
                    <label className="text-xs text-gray-500">
                      {f.label} {isReq ? <span className="text-red-600">*</span> : null}
                    </label>
                    {f.widget === "textarea" ? (
                      <textarea
                        className={`border rounded p-2 text-sm h-24 ${fieldErr ? "border-red-400" : ""}`}
                        value={safeString(previewForm[f.key])}
                        onChange={(e) => {
                          const val = e.target.value;
                          setPreviewForm((p) => ({ ...p, [f.key]: val }));
                          setPreviewFieldErrs((p) => {
                            const next = { ...p };
                            delete next[f.key];
                            return next;
                          });
                        }}
                        placeholder={f.placeholder}
                      />
                    ) : (
                      <input
                        className={`border rounded p-2 text-sm ${fieldErr ? "border-red-400" : ""}`}
                        value={safeString(previewForm[f.key])}
                        onChange={(e) => {
                          const val = e.target.value;
                          setPreviewForm((p) => ({ ...p, [f.key]: val }));
                          setPreviewFieldErrs((p) => {
                            const next = { ...p };
                            delete next[f.key];
                            return next;
                          });
                        }}
                        placeholder={f.placeholder}
                      />
                    )}
                    {fieldErr ? <div className="mt-1 text-xs text-red-700">{fieldErr}</div> : null}
                  </div>
                );
              })}
            </div>
          )}

          {previewErr && <div className="mt-4 text-sm text-red-700 whitespace-pre-wrap">{previewErr}</div>}
          {previewOut && (
            <div className="mt-4 space-y-3">
              <div>
                <div className="text-sm font-medium">Final Answer</div>
                <div className="mt-2 border rounded bg-gray-50 p-3 text-sm whitespace-pre-wrap">{extractPreviewFinalAnswer(previewOut) ?? "(No primary output detected)"}</div>

                {(previewOut.final_output_kind || previewOut.final_output_path) && (
                  <div className="mt-2 text-xs text-gray-500">
                    {previewOut.final_output_kind ? (
                      <>
                        kind: <code>{String(previewOut.final_output_kind)}</code> ·{" "}
                      </>
                    ) : null}
                    {previewOut.final_output_path ? (
                      <>
                        path: <code>{String(previewOut.final_output_path)}</code>
                      </>
                    ) : null}
                  </div>
                )}
              </div>

              <details>
                <summary className="cursor-pointer text-sm text-gray-700">Raw output (JSON)</summary>
                <pre className="mt-2 text-xs bg-gray-50 border rounded p-3 overflow-auto max-h-[520px]">{JSON.stringify(previewOut, null, 2)}</pre>
              </details>
            </div>
          )}
        </div>
      )}

      {/* REQUESTS */}
      {tab === "requests" && (
        <div className="mt-6 space-y-4">
          <div className="rounded border bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-medium">Requests</div>
                <div className="mt-1 text-xs text-gray-500">
                  Admin monitor via <code>GET /v1/admin/ingress/requests?tenant_id={TENANT_UUID}&amp;app_id={app.id}</code>.
                </div>
              </div>
              <Link className="px-3 py-2 rounded border hover:bg-gray-50 text-sm" to={`/app/admin/ingress/requests?app_id=${app.id}`}>
                Open Full Monitor
              </Link>
            </div>

            {loadingRequests ? (
              <div className="mt-4 text-sm text-gray-500">Loading requests…</div>
            ) : requests.length === 0 ? (
              <div className="mt-4 text-sm text-gray-600">No requests yet.</div>
            ) : (
              <div className="mt-4 overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-xs text-gray-500">
                    <tr className="border-b">
                      <th className="text-left py-2 pr-3">Request ID</th>
                      <th className="text-left py-2 pr-3">Status</th>
                      <th className="text-left py-2 pr-3">Created</th>
                      <th className="text-left py-2 pr-3">Run ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((r) => (
                      <tr key={r.id} className="border-b">
                        <td className="py-2 pr-3 font-mono">
                          <Link className="text-blue-700 hover:underline" to={`/app/admin/ingress/requests/${r.id}`}>
                            {r.id}
                          </Link>
                        </td>
                        <td className="py-2 pr-3">
                          <span className="px-2 py-1 rounded border text-xs">{r.status}</span>
                        </td>
                        <td className="py-2 pr-3">{fmtDt(r.created_at)}</td>
                        <td className="py-2 pr-3 font-mono">{r.run_id ? r.run_id : <span className="text-gray-400">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 text-xs text-gray-500">Click a request ID to view detail/output in the Requests module.</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CONNECTIONS (External App Connections) */}
      {tab === "connections" && (
        <div className="mt-6 space-y-4">
          <div className="rounded border bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium">External App Connections</div>
                <div className="mt-1 text-xs text-gray-500">
                  Connect external callers to this ingress app via <code>X-Ingress-Key</code>.
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Endpoint: <code>POST /v1/ingress/apps/{app.slug}/requests</code>
                </div>
              </div>
              <button className="px-3 py-2 rounded border hover:bg-gray-50 text-sm disabled:opacity-60" onClick={loadConnections} disabled={connLoading} type="button">
                {connLoading ? "Refreshing…" : "Refresh"}
              </button>
            </div>

            {connErr && <div className="mt-3 text-sm text-red-700 whitespace-pre-wrap">{connErr}</div>}

            {/* Create */}
            <div className="mt-4 border rounded p-4">
              <div className="font-medium text-sm">Create Connection</div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-600">Name *</div>
                  <input className="mt-1 w-full border rounded p-2 text-sm" value={newConnName} onChange={(e) => setNewConnName(e.target.value)} placeholder='e.g. "Zapier - Lead Enrichment"' />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Type</div>
                  <select className="mt-1 w-full border rounded p-2 text-sm" value={newConnType} onChange={(e) => setNewConnType(e.target.value)}>
                    {["custom", "zapier", "slack", "salesforce", "webhook", "internal_service"].map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Base URL</div>
                  <input className="mt-1 w-full border rounded p-2 text-sm" value={newConnBaseUrl} onChange={(e) => setNewConnBaseUrl(e.target.value)} placeholder="https://..." />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Notes</div>
                  <input className="mt-1 w-full border rounded p-2 text-sm" value={newConnNotes} onChange={(e) => setNewConnNotes(e.target.value)} placeholder="Optional" />
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <button className="px-3 py-2 rounded bg-gray-900 text-white hover:bg-black text-sm" onClick={createConnection} type="button">
                  Create & Generate Key
                </button>
              </div>

              {createdConnKey && (
                <div className="mt-4 border rounded p-3 bg-green-50 border-green-200">
                  <div className="text-sm font-medium text-green-800">New key created (shown once)</div>
                  <div className="mt-2 text-xs text-gray-700">Store this securely. You won’t be able to view it again after leaving this page.</div>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="text-xs break-all">{createdConnKey.api_key}</code>
                    <button className="px-2 py-1 rounded border text-xs hover:bg-white" onClick={async () => { await copyToClipboard(createdConnKey.api_key); }} type="button">
                      Copy
                    </button>
                  </div>
                  <div className="mt-3 text-xs text-gray-700">Example header: <code>X-Ingress-Key: {createdConnKey.api_key}</code></div>
                  <div className="mt-2 text-xs text-gray-700 whitespace-pre-wrap">
                    Example curl:{" "}
                    <code className="break-all">{`curl -sS -X POST "http://localhost:8000/v1/ingress/apps/${app.slug}/requests?tenant_id=${TENANT_UUID}" -H "X-Tenant-ID: root" -H "X-Ingress-Key: ${createdConnKey.api_key}" -H "Content-Type: application/json" -d '{"inputs":{...}}'`}</code>
                  </div>
                </div>
              )}
            </div>

            {/* List */}
            <div className="mt-4 border rounded overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-50">
                <div className="col-span-3">Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-3">Base URL</div>
                <div className="col-span-2">Key Prefix</div>
                <div className="col-span-2">Actions</div>
              </div>

              {connLoading ? (
                <div className="p-4 text-sm text-gray-600">Loading…</div>
              ) : connections.length === 0 ? (
                <div className="p-4 text-sm text-gray-600">No connections yet.</div>
              ) : (
                connections.map((c) => (
                  <div key={c.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t text-sm">
                    <div className="col-span-3">
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="text-[11px] text-gray-500 mt-1">
                        created: {fmtDt(c.created_at)} · last_used: {c.last_used_at ? fmtDt(c.last_used_at) : "—"}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-1">
                        last_test: {c.last_test_status || "never"}{c.last_test_at ? ` · ${fmtDt(c.last_test_at)}` : ""}
                      </div>
                    </div>
                    <div className="col-span-2 text-xs text-gray-700">{c.type}</div>
                    <div className="col-span-3 text-xs text-gray-700 truncate" title={c.base_url || ""}>
                      {c.base_url || "—"}
                    </div>
                    <div className="col-span-2 text-xs text-gray-700">
                      <code>{c.key_prefix}</code>
                      {c.revoked_at ? <span className="ml-2 text-[11px] text-gray-500">(revoked)</span> : null}
                    </div>
                    <div className="col-span-2 flex gap-2">
                      <button className="px-2 py-1 rounded border text-xs hover:bg-gray-50 disabled:opacity-60" onClick={() => testConnection(c.id)} disabled={testingConnId === c.id || !!c.revoked_at} type="button">
                        {testingConnId === c.id ? "Testing…" : "Test"}
                      </button>
                      <button className="px-2 py-1 rounded border text-xs hover:bg-gray-50 disabled:opacity-60" onClick={() => rotateConnection(c.id)} disabled={rotatingConnId === c.id} type="button">
                        {rotatingConnId === c.id ? "Rotating…" : "Rotate"}
                      </button>
                      <button className="px-2 py-1 rounded border text-xs hover:bg-gray-50 disabled:opacity-60" onClick={() => revokeConnection(c.id)} disabled={revokingConnId2 === c.id || !!c.revoked_at} type="button">
                        {revokingConnId2 === c.id ? "Revoking…" : "Revoke"}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Smoke test result */}
            {testResult && (
              <div className="mt-4 border rounded p-4">
                <div className="font-medium text-sm">Smoke Test Result</div>
                <div className="mt-2 text-sm">
                  ok: <b>{String(testResult.ok)}</b> · status: <b>{String(testResult.status || "")}</b>
                </div>
                {testResult.request_id && (
                  <div className="mt-1 text-xs text-gray-600">
                    request_id: <code>{testResult.request_id}</code>
                  </div>
                )}
                {testResult.error && <div className="mt-2 text-sm text-red-700 whitespace-pre-wrap">{testResult.error}</div>}
                {testResult.final_output !== undefined && (
                  <pre className="mt-3 text-xs bg-gray-50 border rounded p-3 overflow-auto max-h-[320px]">
                    {JSON.stringify(
                      {
                        final_output_kind: testResult.final_output_kind,
                        final_output_path: testResult.final_output_path,
                        final_output: testResult.final_output,
                        timings_ms: testResult.timings_ms,
                      },
                      null,
                      2
                    )}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* API KEYS (Universal Plug-In Ingress) */}
      {tab === "api_keys" && (
        <div className="mt-6 space-y-4">
          <div className="rounded border bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium">API Keys</div>
                <div className="mt-1 text-xs text-gray-500">
                  Manage external access for this ingress app. Callers include:
                  <code className="ml-2">X-Ingress-Key: ing_...</code>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Endpoint: <code>POST /v1/ingress/apps/{app.slug}/requests</code>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="px-3 py-2 rounded border hover:bg-gray-50 text-sm disabled:opacity-60" onClick={loadApiKeys} disabled={keysLoading} type="button">
                  {keysLoading ? "Refreshing…" : "Refresh"}
                </button>
                <button className="px-3 py-2 rounded bg-gray-900 text-white hover:bg-black text-sm disabled:opacity-60" onClick={createApiKey} disabled={creatingKey} type="button">
                  {creatingKey ? "Creating…" : "Create key"}
                </button>
              </div>
            </div>

            {keysErr && <div className="mt-3 text-sm text-red-700 whitespace-pre-wrap">{keysErr}</div>}

            {createdKey && (
              <div className="mt-4 border rounded p-3 bg-green-50 border-green-200">
                <div className="text-sm font-medium text-green-800">New key created (shown once)</div>
                <div className="mt-2 text-xs text-gray-700">Store this securely. You won’t be able to view it again after leaving this page.</div>
                <div className="mt-2 flex items-center gap-2">
                  <code className="text-xs break-all">{createdKey.api_key}</code>
                  <button className="px-2 py-1 rounded border text-xs hover:bg-white" onClick={async () => { await copyToClipboard(createdKey.api_key); }} type="button">
                    Copy
                  </button>
                </div>
                <div className="mt-3 text-xs text-gray-700">Example header: <code>X-Ingress-Key: {createdKey.api_key}</code></div>
              </div>
            )}

            <div className="mt-4 border rounded overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-50">
                <div className="col-span-4">Key</div>
                <div className="col-span-3">Created</div>
                <div className="col-span-3">Revoked</div>
                <div className="col-span-2">Action</div>
              </div>

              {keysLoading ? (
                <div className="p-4 text-sm text-gray-600">Loading…</div>
              ) : keys.length === 0 ? (
                <div className="p-4 text-sm text-gray-600">No keys yet. Create one to enable external callers.</div>
              ) : (
                keys.map((k) => (
                  <div key={k.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t text-sm">
                    <div className="col-span-4">
                      <div className="text-xs text-gray-800">prefix: <code>{k.key_prefix}</code></div>
                      <div className="text-[11px] text-gray-500 mt-1">key_id: <code>{k.id}</code></div>
                    </div>
                    <div className="col-span-3 text-xs text-gray-700">{fmtDt(k.created_at)}</div>
                    <div className="col-span-3 text-xs text-gray-700">{k.revoked_at ? fmtDt(k.revoked_at) : "—"}</div>
                    <div className="col-span-2">
                      {k.revoked_at ? (
                        <span className="text-xs text-gray-500">revoked</span>
                      ) : (
                        <button className="px-2 py-1 rounded border text-xs hover:bg-gray-50 disabled:opacity-60" onClick={() => revokeApiKey(k.id)} disabled={revokingKeyId === k.id} type="button">
                          {revokingKeyId === k.id ? "Revoking…" : "Revoke"}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-4 text-xs text-gray-500">
              Tip: Set <code>auth_mode</code> to <code>api_key</code> (Overview → Edit) to require keys.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn(props: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      className={`px-3 py-2 text-sm border-b-2 ${
        props.active ? "border-gray-900 text-gray-900 font-medium" : "border-transparent text-gray-500 hover:text-gray-800"
      }`}
      onClick={props.onClick}
      type="button"
    >
      {props.label}
    </button>
  );
}

function KV(props: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-gray-600">{props.label}:</div>
      <div className="text-gray-900">{props.value}</div>
    </div>
  );
}

function FieldRow(props: {
  label: string;
  value: string;
  edit: boolean;
  onChange: (v: string) => void;
  mono?: boolean;
  type?: string;
}) {
  return (
    <div>
      <div className="text-gray-600 text-xs">{props.label}</div>
      {props.edit ? (
        <input
          className={`mt-1 w-full border rounded p-2 text-sm ${props.mono ? "font-mono" : ""}`}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          type={props.type || "text"}
        />
      ) : (
        <div className={`mt-1 ${props.mono ? "font-mono text-sm" : "text-sm"}`}>
          {props.value ? props.value : <span className="text-gray-400">—</span>}
        </div>
      )}
    </div>
  );
}